// In-app issue/question reports (hamburger menu → "Report a Problem").
// Pilot support channel: any signed-in user can file a report; docs land in
// the `issueReports` collection, readable only by the product owner allow-list
// in firestore.rules (same perimeter as pilot usage analytics — reports are
// product feedback, not school admin data).
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { useRoute } from 'vue-router'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { useSchoolStore } from '@/store/schoolStore'
import { devLog } from '@/utils/environment'

// Keep in sync with the `validIssueReport()` type list in firestore.rules.
export const ISSUE_REPORT_TYPES = [
  { id: 'problem', label: 'Report a problem' },
  { id: 'question', label: 'Ask a question' },
  { id: 'suggestion', label: 'Share a suggestion' }
]

export const ISSUE_REPORT_MAX_LENGTH = 5000

export function useIssueReports() {
  const authStore = useAuthStore()
  const schoolStore = useSchoolStore()
  const route = useRoute()

  async function submitIssueReport({ type, message }) {
    const user = authStore.currentUser
    if (!user?.uid) throw new Error('Not signed in')
    if (!ISSUE_REPORT_TYPES.some((t) => t.id === type)) throw new Error('Invalid report type')

    const trimmed = (message || '').trim()
    if (!trimmed) throw new Error('Message is required')
    if (trimmed.length > ISSUE_REPORT_MAX_LENGTH) throw new Error('Message is too long')

    // Every key here must stay within the closed field list in
    // validIssueReport() (firestore.rules) or the write is rejected.
    const report = {
      type,
      message: trimmed,
      route: route?.fullPath || '',
      userId: user.uid,
      userEmail: user.email || '',
      userName: user.name || '',
      userRole: user.role || '',
      schoolId: schoolStore.currentSchoolId || '',
      host: window.location.hostname,
      userAgent: navigator.userAgent,
      status: 'new',
      createdAt: serverTimestamp()
    }

    devLog('🐛 Submitting issue report', { type, route: report.route })
    await addDoc(collection(db, 'issueReports'), report)
  }

  return { submitIssueReport }
}
